/**
 * Keeps the Smart Auto Scroll schedule in step with what's on screen.
 *
 * The schedule is built from measured geometry, so anything that reflows the page invalidates it:
 * a new song, a font size change, a rotation, Hide Chords, Overview. Each of those rebuilds it.
 * The scroll engine reads the schedule fresh every frame and carries its own position in *time*,
 * so a rebuild mid-song re-derives where the page should be without losing the place in the music.
 *
 * Mounted in ViewBody, which is the component that renders the parts being measured.
 */

import { useEffect, useState } from 'react';

import { buildSchedule, clearSchedule, setSchedule } from '../utils/smartScrollSchedule';
import { getSetting, settingKeys } from '../utils/settings';

// Long enough that a drag-resize or an orientation change settles first, short enough that the
// schedule is ready before anyone reaches for the scroll button
const REBUILD_DEBOUNCE_MS = 150;

/** Fired by the settings modal so an open View picks the toggle up without a remount. */
export const SMART_SCROLL_CHANGED_EVENT = 'smartScrollChanged';

export const isSmartScrollEnabled = () => Boolean(getSetting(settingKeys.smartScroll));

const useSmartScrollSchedule = (songs, { hideChords, overview } = {}) => {
  const [enabled, setEnabled] = useState(isSmartScrollEnabled);

  useEffect(() => {
    const syncEnabled = () => setEnabled(isSmartScrollEnabled());
    window.addEventListener(SMART_SCROLL_CHANGED_EVENT, syncEnabled);
    return () => window.removeEventListener(SMART_SCROLL_CHANGED_EVENT, syncEnabled);
  }, []);

  // The songs array is rebuilt on every ViewBody render, so depend on the ids instead
  const songKey = (songs || []).map((song) => song?._id).join(',');

  useEffect(() => {
    if (!enabled) {
      clearSchedule();
      return undefined;
    }

    let frameId = null;
    let timeoutId = null;

    // Measure after the browser has laid the page out, not during the commit that changed it
    const rebuild = () => {
      frameId = requestAnimationFrame(() => {
        frameId = null;
        setSchedule(buildSchedule(songs || []));
      });
    };

    const rebuildSoon = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(rebuild, REBUILD_DEBOUNCE_MS);
    };

    rebuild();

    // The same event ViewBody already listens to for its own font size
    window.addEventListener('fontSizeChanged', rebuildSoon);
    window.addEventListener('resize', rebuildSoon);
    window.addEventListener('orientationchange', rebuildSoon);

    return () => {
      window.removeEventListener('fontSizeChanged', rebuildSoon);
      window.removeEventListener('resize', rebuildSoon);
      window.removeEventListener('orientationchange', rebuildSoon);
      clearTimeout(timeoutId);
      if (frameId !== null) cancelAnimationFrame(frameId);
      clearSchedule();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, songKey, hideChords, overview]);
};

export default useSmartScrollSchedule;
